import { useMemo, useState } from 'react';
import { Form, Formik, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import type { FolderFormValues, FolderCreateModalFormProps } from './types';
import {
  canSave,
  buildFolderPayload,
  buildInitialFormValues,
} from './folderFormState';
import { FolderChildDataSync } from './FolderChildDataSync';
import { FolderTravellersSection } from './FolderTravellersSection';
import { FolderTravellerDetailsSection } from './FolderTravellerDetailsSection';
import { FolderFooterHint } from './FolderFooterHint';

const FormField = ({
  name,
  children,
}: {
  name: string;
  children: (field: any) => React.ReactNode;
}) => {
  const [field, meta] = useField(name);
  const hasError = Boolean(meta.error);
  return (
    <>
      {children(field)}
      {hasError && (
        <div className="text-red-500 text-xs mt-1">{meta.error}</div>
      )}
    </>
  );
};

export type { FolderCreateModalFormProps };

export default function FolderCreateModalForm({
  onSubmit,
  isPending,
  onImport,
  onCancel,
  hideImportButton = true,
  canEditIcr = true,
  initialValues,
  renderTextField,
  renderSelectField,
  renderDateField,
  renderNationalityField,
  renderCheckboxField,
  renderLanguageField,
  extraFields,
  submitLabel,
  renderSubmitArea,
  onRegisterCloseGuard,
  requireCompleteTravellers = false,
}: FolderCreateModalFormProps) {
  const { t } = useTranslation(['folders', 'customerFile', 'errors', 'misc']);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const defaultInitialValues = useMemo<FolderFormValues>(
    () => buildInitialFormValues(initialValues),
    [initialValues],
  );

  const handleSubmit = (values: FolderFormValues) => {
    const payload = buildFolderPayload(values);
    onSubmit(payload);
  };

  return (
    <Formik
      initialValues={defaultInitialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {(formikProps) => {
        const { values, setFieldValue, dirty } = formikProps;
        const canSaveState = canSave(values, { requireCompleteTravellers });

        const handleCancelAttempt = () => {
          if (dirty) {
            setConfirmDiscardOpen(true);
          } else if (onCancel) {
            onCancel();
          }
        };

        // Esc and the overlay close the modal from the app side, which knows
        // nothing about the form being dirty: hand it the same guard.
        if (onRegisterCloseGuard) {
          onRegisterCloseGuard(handleCancelAttempt);
        }

        return (
          <Form className="relative flex min-h-[min(500px,60vh)] flex-col">
            <FolderChildDataSync
              values={values}
              setFieldValue={setFieldValue}
            />
            {/* The modal keeps a fixed height: only this body scrolls, so the
                header above it and the footer below stay in place. */}
            <div className="max-h-[min(68vh,calc(100vh-20rem))] flex-1 space-y-6 overflow-y-auto overscroll-contain px-1 pb-6">
              {/* Section 1: Folder Information */}
              <div className="space-y-4">
                <div className="border-b border-gray-200 pb-1">
                  <span className="text-xs font-bold tracking-wider text-orange-600 uppercase">
                    {t('folderInformationSection')}
                  </span>
                </div>

                <div
                  className={`grid ${
                    renderLanguageField ? 'grid-cols-3' : 'grid-cols-2'
                  } gap-5`}
                >
                  {renderTextField ? (
                    renderTextField({
                      label: t('customerFile:name'),
                      name: 'name',
                      required: true,
                      touchRequired: false,
                      placeholder: 'e.g. Martin family — Kenya',
                      errors: formikProps.errors,
                      touched: formikProps.touched,
                    })
                  ) : (
                    <div>
                      <label className="block text-sm font-semibold mb-1 text-gray-800">
                        {t('customerFile:name')}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={(e) => setFieldValue('name', e.target.value)}
                        placeholder="e.g. Martin family — Kenya"
                        className="w-full h-11 px-3.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  )}

                  <div className="flex items-start w-full">
                    <div className="flex-1">
                      {renderTextField ? (
                        renderTextField({
                          label: t('customerFile:icr'),
                          name: 'icr',
                          required: true,
                          touchRequired: false,
                          placeholder: 'e.g. MART-2026-01',
                          disabled: !canEditIcr,
                          errors: formikProps.errors,
                          touched: formikProps.touched,
                        })
                      ) : (
                        <div>
                          <label className="block text-sm font-semibold mb-1 text-gray-800">
                            {t('customerFile:icr')}{' '}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="icr"
                            value={values.icr}
                            disabled={!canEditIcr}
                            onChange={(e) =>
                              setFieldValue('icr', e.target.value)
                            }
                            placeholder="e.g. MART-2026-01"
                            className="w-full h-11 px-3.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
                          />
                        </div>
                      )}
                    </div>
                    {onImport && !hideImportButton && !values.id && (
                      <div className="pt-[24px] ml-3">
                        <button
                          type="button"
                          onClick={() => onImport(values)}
                          disabled={!canEditIcr || !values.icr}
                          className="bg-gradient-to-b from-orange-500 to-orange-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium border-none cursor-pointer disabled:opacity-50 hover:brightness-95 transition"
                        >
                          {t('customerFile:modal.importButton')}
                        </button>
                      </div>
                    )}
                  </div>

                  {renderLanguageField && (
                    <div>{renderLanguageField(formikProps)}</div>
                  )}
                </div>
              </div>

              {/* Section 2: Additional Details (DMC option / Extra fields) */}
              {extraFields && (
                <div className="space-y-4">
                  <div className="border-b border-gray-200 pb-1">
                    <span className="text-xs font-bold tracking-wider text-orange-600 uppercase">
                      {t('customerFile:modal.sections.additionalDetails')}
                    </span>
                  </div>
                  <div>{extraFields(formikProps)}</div>
                </div>
              )}

              {/* Section 3: Travellers (Group Size, Children Ages, Room Composition) */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center border-b border-gray-200 pb-1">
                  <span className="text-xs font-bold tracking-wider text-orange-600 uppercase">
                    {t('travellersSection')}
                  </span>
                  <span className="ml-auto text-xs font-medium normal-case text-gray-500">
                    {t('travellersSummary', {
                      travellers: values.adults + values.children.length,
                      rooms: values.rooms.length,
                    })}
                  </span>
                </div>
                <FolderTravellersSection
                  values={values}
                  setFieldValue={setFieldValue}
                  t={t}
                />
              </div>

              {/* Section 4: Traveller Details (Collapsible Adult / Child cards) */}
              <div className="space-y-4 pt-4">
                <div className="border-b border-gray-200 pb-1">
                  <span className="text-xs font-bold tracking-wider text-orange-600 uppercase">
                    {t('travellerDetailsSection')}
                  </span>
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide uppercase ${
                      requireCompleteTravellers
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {requireCompleteTravellers
                      ? t('requiredBadge')
                      : t('optionalBadge')}
                  </span>
                </div>
                <FolderTravellerDetailsSection
                  values={values}
                  setFieldValue={setFieldValue}
                  renderTextField={
                    renderTextField
                      ? (name, label) =>
                          renderTextField({
                            name,
                            label,
                            errors: formikProps.errors,
                            touched: formikProps.touched,
                          })
                      : undefined
                  }
                  renderSelectField={
                    renderSelectField
                      ? (name, label, options) =>
                          renderSelectField({
                            name,
                            label,
                            options,
                            errors: formikProps.errors,
                            touched: formikProps.touched,
                          })
                      : undefined
                  }
                  renderDateField={
                    renderDateField
                      ? (name, label) =>
                          renderDateField({
                            name,
                            label,
                            errors: formikProps.errors,
                            touched: formikProps.touched,
                          })
                      : undefined
                  }
                  renderNationalityField={
                    renderNationalityField
                      ? (name, label) =>
                          renderNationalityField({
                            name,
                            label,
                            errors: formikProps.errors,
                            touched: formikProps.touched,
                          })
                      : undefined
                  }
                  t={t}
                  requireCompleteTravellers={requireCompleteTravellers}
                />
              </div>
            </div>

            {/* Footer: outside the scroll area, always visible */}
            <div className="sticky bottom-0 z-10 mt-auto flex shrink-0 items-center justify-between rounded-b-xl border-t border-gray-200 bg-white px-6 py-4">
              <div className="flex-1 pr-4">
                <FolderFooterHint
                  values={values}
                  t={t}
                  requireCompleteTravellers={requireCompleteTravellers}
                />
              </div>

              <div className="flex items-center space-x-3 shrink-0">
                {renderSubmitArea ? (
                  renderSubmitArea({
                    ...formikProps,
                    isValid: canSaveState.ok,
                    // Apps render their own Cancel: give them the guarded
                    // close so unsaved changes still raise the discard dialog.
                    onCancel: handleCancelAttempt,
                  })
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleCancelAttempt}
                      className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
                    >
                      {t('misc:cancel')}
                    </button>

                    <button
                      type="submit"
                      disabled={isPending || !canSaveState.ok}
                      className="px-6 py-2.5 bg-gradient-to-b from-orange-500 to-orange-600 text-white rounded-lg text-sm font-medium border-none cursor-pointer disabled:opacity-50 hover:brightness-95 transition shadow-sm"
                    >
                      {submitLabel ?? t('misc:save')}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Discard changes confirmation modal */}
            {confirmDiscardOpen && (
              <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-xl space-y-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('discardTitle')}
                  </h3>
                  <p className="text-sm text-gray-600">{t('discardMessage')}</p>
                  <div className="flex justify-end space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDiscardOpen(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                    >
                      {t('misc:keepEditing')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmDiscardOpen(false);
                        if (onCancel) onCancel();
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition border-none shadow-sm"
                    >
                      {t('misc:discard')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
}

export { FormField };
